import _ from "lodash";
import {
    PartnerState,
    PartnerEmployer,
    PartnerEmployerStatus,
    CensusManager,
    Communication,
    SaveCommunication,
    EmployerTypeAheadResponse
} from "@/employer/types";
import * as UserService from "@/shared/api/userService";
import * as BankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import {
    CommunicationPortal,
    EmployerCreateResponse,
    EnrollmentOption,
    FetchState,
    FundingOption
} from "@/shared/types";
import { defineStore } from "pinia";
import { useUserStore } from "@/shared/store/user";

const defaultPartnerEmployer: PartnerEmployer = {
    censusManager: CensusManager.none,
    employerId: 0,
    enrolledEmployeeCount: 0,
    externalEmployerId: "",
    name: "",
    fundingOption: FundingOption.None,
    hasFundingOptionHistory: false,
    isTerminated: false,
    status: PartnerEmployerStatus.None,
    taxId: "",
    enrollmentOption: EnrollmentOption.None,
    score: 0
};

export const initialState: PartnerState = {
    fetchState: FetchState.Uninitialized,
    partner: null,
    totalRecords: 0,
    queryTime: 0,
    maxScore: 0,
    employerSearchTypeAheadResult: null,
    employerSearchTypeAheadFetchState: FetchState.Uninitialized,
};

export const usePartnerStore = defineStore("partner", {
    state: (): PartnerState => (_.cloneDeep(initialState)),
    getters: {
        totalEmployers: state => state.totalRecords,
        employersLoaded: state => () => {
            return state.fetchState === FetchState.Loaded;
        },
        partnerFetchState: state => () => {
            return state.fetchState;
        },
        employers: state => () => {
            const partner = state.partner;

            if (partner && partner.employers) {
                return partner.employers;
            }

            return [];
        },
        partnerEmployerLoaded: state => (employerId): boolean => {
            if (state.partner && state.partner.employers) {
                const foundEmployer = state.partner.employers.find(t => t.employerId === employerId);
                return foundEmployer ? true : false;
            }
            return false;
        },
        selectedEmployerByEmployerId: state => (employerId): PartnerEmployer => {
            if (state.partner && state.partner.employers) {
                const foundEmployer = state.partner.employers.find(t => t.employerId === employerId);
                if (foundEmployer) {
                    return foundEmployer;
                }
            }
            return defaultPartnerEmployer;
        },
    },
    actions: {
        setEmployers (value: {
            partnerId: number, response:
                { employers: PartnerEmployer[], totalRecords: number, maxScore: number, queryTime: number }
        }) {
            if (!value || !value.partnerId || typeof value.partnerId !== "number") {
                return;
            }

            let partner = this.partner;

            if (!partner || partner.partnerId !== value.partnerId) {
                partner = { partnerId: value.partnerId, employers: new Array<PartnerEmployer>() };
            }

            const employers = value.response.employers;
            partner.employers = employers;
            this.totalRecords = value.response.totalRecords;
            this.queryTime = value.response.queryTime;
            this.maxScore = value.response.maxScore;
            this.partner = { ...partner };
        },
        setFetchState (fetchState) {
            this.fetchState = fetchState;
        },
        resetPartnerFetchState () {
            this.fetchState = FetchState.Uninitialized;
        },
        setEmployerSearchTypeAheadFetchState (fetchState: FetchState) {
            this.employerSearchTypeAheadFetchState = fetchState;
        },
        setEmployerSearchTypeAheadResult (data: EmployerTypeAheadResponse) {
            this.employerSearchTypeAheadResult = data;
        },
        loadEmployerByEmployerId (
            data: { partnerId: number, employerId: number }): Promise<any> {
            if (data.partnerId && this.partner && this.partner.partnerId === data.partnerId) {
                const employerIsLoaded = this.partner.employers.find(t =>
                    t.employerId === data.employerId && t.partnerId === data.partnerId);
                if (employerIsLoaded) {
                    return Promise.resolve();
                }
            }
            return UserService.partnerEmployer(data.partnerId, data.employerId).then(userResponse => {
                const userStore = useUserStore();
                return BankService.getEmployerSuspensionByEmployerId(data.partnerId, data.employerId)
                    .then(bankResponse => {
                        if (bankResponse.data) {
                            if (userResponse.data && bankResponse.data.isSuspended) {
                                userResponse.data.status = PartnerEmployerStatus.Suspended;
                            }
                        }
                        this.setEmployers({
                            partnerId: data.partnerId, response: { employers: [ userResponse.data ], totalRecords: 1 }
                        });
                        userStore.addAccessBasedEmployers([ userResponse.data ]);
                    });
            }).catch(err => {
                return storeHelper.handleError({ error: err, rethrow: false });
            });
        },
        searchEmployers (
            data: {
                partnerId,
                pageNumber,
                pageSize,
                sortOption,
                sortOrder,
                searchTerm
            }): Promise<any> {
            this.setFetchState(FetchState.Loading);
            const userStore = useUserStore();
            return UserService.partnerEmployersSearch(data.partnerId,
                    data.pageNumber,
                    data.pageSize,
                    data.sortOption,
                    data.sortOrder,
                    data.searchTerm)
                .then(userResponse => {
                    return BankService.getEmployerSuspensionByPartnerId(data.partnerId).then(bankResponse => {
                        const combinedResults = userResponse.data;
                        if (bankResponse.data.length) {
                            bankResponse.data.forEach(x => {
                                const suspendedUser = _.find(combinedResults.employers,
                                    y => y.employerId === x.employerId);
                                if (suspendedUser && x.isSuspended) {
                                    suspendedUser.status = PartnerEmployerStatus.Suspended;
                                }
                            });
                        }
                        this.setEmployers({ partnerId: data.partnerId, response: combinedResults });
                        userStore.addAccessBasedEmployers(combinedResults.employers);
                        this.setFetchState(FetchState.Loaded);
                    });
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        postPartnerCreateEmployer (data: {
            partnerId: number,
            companyName: string,
            externalEmployerId: string
        }): Promise<EmployerCreateResponse> {
            return UserService.partnerCreateEmployer(
                data.partnerId,
                data.companyName,
                data.externalEmployerId
            ).catch(err => {
                if (err.response.data.errorMessage && err.response.data.errorMessage.includes("External Employer Id")) {
                    return Promise.reject(err.response.data.errorMessage);
                } else {
                    return storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        },
        postPartnerCommunication (data: {
            partnerId: number,
            portal: CommunicationPortal, communication: Communication
        }): Promise<SaveCommunication> {
            return UserService.postPartnerCommunication(data.partnerId, data.portal, data.communication)
                .then(_ => _.data)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadPartnerCommunications (data: { partnerId: number, portal: CommunicationPortal }) {
            const userStore = useUserStore();
            userStore.setCommunicationsFetchState(FetchState.Loading);

            return UserService.loadPartnerCommunications(data.partnerId, data.portal)
                .then(_ => userStore.setCommunications({ portal: data.portal, communications: (_.data) as any }))
                .catch(e => storeHelper.handleError({ error: e, rethrow: false }))
                .finally(() => userStore.setCommunicationsFetchState(FetchState.Loaded));
        },
        saveCommunicationImage (
            { partnerId, communicationId, imageFile, isImageDeleted }): Promise<any> {
            if ((imageFile !== null && imageFile !== undefined && imageFile.length) || isImageDeleted) {
                if (isImageDeleted) {
                    return UserService.removeCommunicationImage(partnerId, communicationId)
                        .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
                } else {
                    return UserService.saveCommunicationImage(partnerId, communicationId, imageFile)
                        .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
                }
            }
            return Promise.resolve();
        },
        imageGetLink ({ partnerId, communicationId }) {
            const newTab = window.open();
            return UserService.getCommunicationImageUrl(partnerId, communicationId).then(response => {
                    newTab.location.href = response.data;
                    setTimeout(() => {
                        if (newTab.location && newTab.location.href === "about:blank") {
                            newTab.close();
                        }
                    }, 2000);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    newTab.close();
                });
        },
        getEmployerSearchTypeAhead (data: { searchTerm: string, partnerId: number }) {
            this.setEmployerSearchTypeAheadFetchState(FetchState.Loading);
            return UserService.getEmployerTypeAhead(data.partnerId, data.searchTerm)
                .then(response => {
                    this.setEmployerSearchTypeAheadResult(response.data);
                    this.setEmployerSearchTypeAheadFetchState(FetchState.Loaded);
                }).catch(err => {
                this.setEmployerSearchTypeAheadFetchState(FetchState.Loaded);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
    }
});